import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Alert,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InputAdornment from '@mui/material/InputAdornment';
import PageLayout from '../components/common/PageLayout';
import { get, post } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const TEMPLATE_FALLBACK_LABELS = {
  sponsorship_request: 'Sponsorship Request',
  event_reminder: 'Event Reminder',
};
function getTemplateLabel(key) {
  if (!key) return '—';
  const norm = String(key || '').toLowerCase();
  return TEMPLATE_FALLBACK_LABELS[norm] || key;
}
const CAMPAIGN_TO_EMAIL = 'icct@icctmemphis.org';
const COUNTDOWN_SECONDS = 60;

// Fill template placeholders with generic values (same as backend for BCC: one message for all)
function fillCampaignPreview(text, campaignProductName) {
  if (!text) return '';
  const name = campaignProductName || 'our initiative';
  return String(text)
    .replace(/\{\{\s*recipient_name\s*\}\}/gi, 'Devotee')
    .replace(/\{\{\s*event_name\s*\}\}/gi, name)
    .replace(/\{\{\s*product_name\s*\}\}/gi, name);
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

function getDateRangeBounds(preset, customStart, customEnd) {
  if (preset === 'all') return { start: null, end: null };
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  if (preset === '7d') {
    const start = new Date(end);
    start.setDate(start.getDate() - 7);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }
  if (preset === '30d') {
    const start = new Date(end);
    start.setDate(start.getDate() - 30);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }
  if (preset === '90d') {
    const start = new Date(end);
    start.setDate(start.getDate() - 90);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }
  if (preset === 'custom' && customStart && customEnd) {
    const start = new Date(customStart);
    start.setHours(0, 0, 0, 0);
    const endDate = new Date(customEnd);
    endDate.setHours(23, 59, 59, 999);
    return { start, end: endDate };
  }
  return { start: null, end: null };
}

export default function TempleCampaignsPage() {
  const { staff } = useAuth();
  const [campaigns, setCampaigns] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [viewDialog, setViewDialog] = React.useState(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [dateRangePreset, setDateRangePreset] = React.useState('all');
  const [customStartDate, setCustomStartDate] = React.useState('');
  const [customEndDate, setCustomEndDate] = React.useState('');

  const loadCampaigns = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await get('/api/temple/campaigns');
      setCampaigns(Array.isArray(res?.campaigns) ? res.campaigns : []);
    } catch (e) {
      setError(e?.message || 'Failed to load campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const { start: rangeStart, end: rangeEnd } = getDateRangeBounds(dateRangePreset, customStartDate, customEndDate);
  const filteredCampaigns = React.useMemo(() => {
    let list = campaigns;
    const q = (searchQuery || '').trim().toLowerCase();
    if (q) {
      list = list.filter((c) => {
        const name = (c.campaign_product_name || '').toLowerCase();
        const idStr = String(c.campaign_product_id || '');
        return name.includes(q) || idStr.includes(q);
      });
    }
    if (rangeStart && rangeEnd) {
      list = list.filter((c) => {
        const d = c.sent_at ? new Date(c.sent_at) : new Date(c.created_at);
        return !Number.isNaN(d.getTime()) && d >= rangeStart && d <= rangeEnd;
      });
    }
    return list;
  }, [campaigns, searchQuery, rangeStart, rangeEnd]);

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Campaigns</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create Campaign
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search campaign product..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 280, maxWidth: 360 }}
          inputProps={{ 'aria-label': 'Search by campaign product name or ID' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Date range</InputLabel>
          <Select
            value={dateRangePreset}
            label="Date range"
            onChange={(e) => setDateRangePreset(e.target.value)}
          >
            {DATE_RANGE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {dateRangePreset === 'custom' && (
          <>
            <TextField
              size="small"
              label="From"
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
            <TextField
              size="small"
              label="To"
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
          </>
        )}
      </Box>

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : campaigns.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            No campaigns yet. Create one to send targeted emails (event reminders, sponsorship requests).
          </Box>
        ) : filteredCampaigns.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            No campaigns match the current filters. Try a different search or date range.
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sent</TableCell>
                <TableCell>Campaign product</TableCell>
                <TableCell>Template</TableCell>
                <TableCell align="right">BCC count</TableCell>
                <TableCell>Created by</TableCell>
                <TableCell padding="none" width={80} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCampaigns.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{formatDate(c.sent_at)}</TableCell>
                  <TableCell>{c.campaign_product_name ? `${c.campaign_product_name} (#${c.campaign_product_id})` : `#${c.campaign_product_id}`}</TableCell>
                  <TableCell>{getTemplateLabel(c.template_key)}</TableCell>
                  <TableCell align="right">{c.bcc_count}</TableCell>
                  <TableCell>{c.created_by || '—'}</TableCell>
                  <TableCell padding="none">
                    <IconButton size="small" onClick={() => setViewDialog(c)} aria-label="View campaign">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {viewDialog && (
        <Dialog open onClose={() => setViewDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Campaign details
            <IconButton aria-label="Close" onClick={() => setViewDialog(null)} size="small" sx={{ mr: -1 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ pt: 0 }}>
              <Typography variant="body2"><strong>ID:</strong> {viewDialog.id}</Typography>
              <Typography variant="body2"><strong>Campaign product:</strong> {viewDialog.campaign_product_name ? `${viewDialog.campaign_product_name} (#${viewDialog.campaign_product_id})` : `#${viewDialog.campaign_product_id}`}</Typography>
              <Typography variant="body2"><strong>Template:</strong> {viewDialog.template_key}</Typography>
              <Typography variant="body2"><strong>To:</strong> {viewDialog.to_email}</Typography>
              <Typography variant="body2"><strong>BCC count:</strong> {viewDialog.bcc_count}</Typography>
              <Typography variant="body2"><strong>Sent at:</strong> {formatDate(viewDialog.sent_at)}</Typography>
              <Typography variant="body2"><strong>Created at:</strong> {formatDate(viewDialog.created_at)}</Typography>
              <Typography variant="body2"><strong>Created by:</strong> {viewDialog.created_by || '—'}</Typography>
              {Array.isArray(viewDialog.target_product_ids) && viewDialog.target_product_ids.length > 0 && (
                <Typography variant="body2" component="span">
                  <strong>Target products:</strong>{' '}
                  {viewDialog.target_product_ids.map((p) => {
                    const name = [p.product_name, p.variation_name].filter(Boolean).join(' — ') || null;
                    return name ? `${name} (#${p.product_id}${p.variation_id ? ` / ${p.variation_id}` : ''})` : `#${p.product_id}${p.variation_id ? ` / ${p.variation_id}` : ''}`;
                  }).join(', ')}
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialog(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {createOpen && (
        <CreateCampaignDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSent={() => loadCampaigns()}
        />
      )}
    </PageLayout>
  );
}

function CreateCampaignDialog({ open, onClose, onSent }) {
  const [campaignProduct, setCampaignProduct] = React.useState(null);
  const [campaignProductInput, setCampaignProductInput] = React.useState('');
  const [campaignProductOptions, setCampaignProductOptions] = React.useState([]);
  const [templateKey, setTemplateKey] = React.useState('event_reminder');
  const [targetProducts, setTargetProducts] = React.useState([]);
  const [targetInput, setTargetInput] = React.useState('');
  const [targetOptions, setTargetOptions] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);
  const [recipientsResult, setRecipientsResult] = React.useState(null);
  const [recipientsLoading, setRecipientsLoading] = React.useState(false);
  const [recipientsDialogOpen, setRecipientsDialogOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState('');
  const [sendSuccess, setSendSuccess] = React.useState(null);
  const [testSuccess, setTestSuccess] = React.useState('');
  const [countdown, setCountdown] = React.useState(null);
  const [editableSubject, setEditableSubject] = React.useState('');
  const [editableBody, setEditableBody] = React.useState('');

  const selectedTemplate = React.useMemo(
    () => templates.find((t) => String(t.template_key || '').toLowerCase() === String(templateKey || '').toLowerCase()) || null,
    [templates, templateKey]
  );

  React.useEffect(() => {
    if (!selectedTemplate) return;
    setEditableSubject(selectedTemplate.subject_template ?? '');
    setEditableBody(selectedTemplate.body_template ?? '');
  }, [selectedTemplate?.id]);

  const fetchProducts = React.useCallback(async (q, setOptions) => {
    if (!q || q.length < 2) {
      setOptions([]);
      return;
    }
    try {
      const res = await get('/ldb/facets/products', { q, limit: 30 });
      const list = Array.isArray(res?.products) ? res.products : [];
      setOptions(list);
    } catch {
      setOptions([]);
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await get('/api/manage/email-templates', { include_inactive: 'false' });
        const list = Array.isArray(res?.templates) ? res.templates : [];
        if (!cancelled) {
          setTemplates(list);
          // Default to first template when opening dialog, if nothing selected yet.
          if (!templateKey && list[0]?.template_key) {
            setTemplateKey(String(list[0].template_key));
          }
        }
      } catch {
        if (!cancelled) setTemplates([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchProducts(campaignProductInput, setCampaignProductOptions), 300);
    return () => clearTimeout(t);
  }, [open, campaignProductInput, fetchProducts]);

  React.useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchProducts(targetInput, setTargetOptions), 300);
    return () => clearTimeout(t);
  }, [open, targetInput, fetchProducts]);

  React.useEffect(() => {
    if (!open || targetProducts.length === 0) {
      setRecipientsResult(null);
      return;
    }
    let cancelled = false;
    setRecipientsLoading(true);
    post('/api/temple/campaigns/unique-recipients', { product_ids: targetProducts.map((p) => ({ product_id: p.product_id, variation_id: p.variation_id || null })) })
      .then((data) => { if (!cancelled) setRecipientsResult(data); })
      .catch(() => { if (!cancelled) setRecipientsResult({ count: 0, recipients: [], per_product: [] }); })
      .finally(() => { if (!cancelled) setRecipientsLoading(false); });
    return () => { cancelled = true; };
  }, [open, targetProducts]);

  const addTargetProduct = (product) => {
    if (!product?.id) return;
    if (targetProducts.length >= 5) return;
    if (targetProducts.some((p) => p.product_id === product.id)) return;
    setTargetProducts((prev) => [...prev, { product_id: product.id, product_name: product.name || `Product #${product.id}`, variation_id: null }]);
    setTargetInput('');
  };

  const removeTargetProduct = (index) => {
    setTargetProducts((prev) => prev.filter((_, i) => i !== index));
  };

  // Frontend guard: keep this wide-open and rely on backend validation.
  // Buttons are only blocked while a send/countdown is in progress.
  const canSend = true;

  const handleSend = () => {
    setSendError('');
    setSendSuccess(null);
    setSending(true);
    const subject = editableSubject.trim();
    const body = editableBody.trim();
    post('/api/temple/campaigns/send', {
      campaign_product_id: campaignProduct.id,
      campaign_product_name: campaignProduct.name || '',
      template_key: templateKey,
      target_product_ids: targetProducts.map((p) => ({ product_id: p.product_id, variation_id: p.variation_id || null, product_name: p.product_name || null })),
      subject,
      body,
    })
      .then((data) => {
        setSendSuccess({ count: data?.sent_count ?? 0 });
        setSending(false);
        setCountdown(null);
        onSent();
      })
      .catch((e) => {
        setSendError(e?.message || 'Send failed');
        setSending(false);
      });
  };

  const startCountdown = () => {
    if (countdown !== null) return;
    setCountdown(COUNTDOWN_SECONDS);
  };

  const stopCountdown = () => {
    setCountdown(null);
  };

  React.useEffect(() => {
    if (countdown === null || countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // When countdown hits 0, auto-send (no "Send now" click). Show "Please wait…" until success/error.
  React.useEffect(() => {
    if (countdown !== 0 || sending || sendSuccess != null || sendError) return;
    if (!campaignProduct?.id || !templateKey || !selectedTemplate || targetProducts.length === 0 || !recipientsResult || recipientsResult.count === 0 || !editableSubject.trim() || !editableBody.trim()) return;
    handleSend();
  }, [countdown, sending, sendSuccess, sendError]);

  const handleClose = () => {
    setCampaignProduct(null);
    setCampaignProductInput('');
    setTemplateKey('event_reminder');
    setTargetProducts([]);
    setTargetInput('');
    setRecipientsResult(null);
    setSendError('');
    setSendSuccess(null);
    setCountdown(null);
    setSending(false);
    setEditableSubject('');
    setEditableBody('');
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <Dialog open onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Create Campaign
          <IconButton aria-label="Close" onClick={handleClose} size="small" sx={{ mr: -1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 0 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Campaign product (e.g. event or sponsorship being promoted)</Typography>
              <Autocomplete
                size="small"
                options={campaignProductOptions}
                getOptionLabel={(opt) => opt?.name || `Product #${opt?.id}` || ''}
                value={campaignProduct}
                inputValue={campaignProductInput}
                onInputChange={(_, v) => setCampaignProductInput(v)}
                onChange={(_, v) => setCampaignProduct(v)}
                renderInput={(params) => <TextField {...params} placeholder="Search product" />}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Email template</Typography>
              <Autocomplete
                size="small"
                fullWidth
                options={templates}
                getOptionLabel={(opt) => opt?.name || opt?.template_key || ''}
                value={selectedTemplate || null}
                onChange={(_, value) => {
                  if (value?.template_key) {
                    setTemplateKey(String(value.template_key).toLowerCase());
                  } else {
                    setTemplateKey('');
                    setEditableSubject('');
                    setEditableBody('');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Template"
                    placeholder="Search template"
                  />
                )}
                isOptionEqualToValue={(a, b) => String(a?.template_key || '').toLowerCase() === String(b?.template_key || '').toLowerCase()}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Target users: add up to 5 source products (unique users from orders with valid email)</Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 280 }}
                  options={targetOptions}
                  getOptionLabel={(opt) => opt?.name || `Product #${opt?.id}` || ''}
                  inputValue={targetInput}
                  onInputChange={(_, v) => setTargetInput(v)}
                  onChange={(_, v) => { if (v) addTargetProduct(v); }}
                  renderInput={(params) => <TextField {...params} placeholder="Search product to add" />}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                />
                {targetProducts.map((p, i) => (
                  <Chip key={`${p.product_id}-${i}`} label={p.product_name || `#${p.product_id}`} onDelete={() => removeTargetProduct(i)} size="small" />
                ))}
              </Stack>
              {recipientsLoading && <Typography variant="caption" color="text.secondary">Loading recipient count…</Typography>}
              {!recipientsLoading && recipientsResult && (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Unique users</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(recipientsResult.per_product || []).map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{targetProducts[i]?.product_name || `Product #${row.product_id}`}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ fontWeight: 600 }}>
                      <TableCell>Combined (unique)</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => setRecipientsDialogOpen(true)} disabled={recipientsResult.count === 0}>
                          {recipientsResult.count}
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </Box>

            {selectedTemplate && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Subject and body (one message for all BCC recipients — no per-recipient personalization). Placeholders like {`{{product_name}}`} and {`{{recipient_name}}`} are replaced when sent.</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary">To: {CAMPAIGN_TO_EMAIL}</Typography>
                  <Typography variant="caption" display="block" color="text.secondary">BCC: {recipientsResult?.count ?? 0} recipient(s)</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Subject"
                    value={editableSubject}
                    onChange={(e) => setEditableSubject(e.target.value)}
                    sx={{ mt: 1.5 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Message body"
                    multiline
                    minRows={6}
                    value={editableBody}
                    onChange={(e) => setEditableBody(e.target.value)}
                    sx={{ mt: 1.5 }}
                    inputProps={{ style: { whiteSpace: 'pre-wrap' } }}
                  />
                </Paper>
              </Box>
            )}

            {sendError && <Alert severity="error" onClose={() => setSendError('')}>{sendError}</Alert>}
            {sendSuccess != null && (
              <Alert severity="success" onClose={() => setSendSuccess(null)} sx={{ mt: 1 }}>
                Email sent successfully to {sendSuccess.count} recipient(s).
              </Alert>
            )}
            {testSuccess && (
              <Alert severity="success" onClose={() => setTestSuccess('')} sx={{ mt: sendSuccess ? 1 : 2 }}>
                {testSuccess}
              </Alert>
            )}

            {countdown !== null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                {countdown > 0 ? (
                  <>
                    <Typography
                      sx={{
                        color: 'error.main',
                        fontWeight: 700,
                        '@keyframes countdownPulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.65 },
                        },
                        animation: 'countdownPulse 1.2s ease-in-out infinite',
                      }}
                    >
                      Sending in <strong>{countdown}</strong> seconds…
                    </Typography>
                    <Tooltip title="Stop countdown">
                      <IconButton color="error" onClick={stopCountdown} aria-label="Stop countdown" disabled={sending}>
                        <StopIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Please wait while we are sending the message…
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={handleClose} disabled={sending || countdown !== null}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              if (sending || countdown !== null) return;
              setSendError('');
              // Keep any real-send success visible; track test separately
              setSending(true);
              const subject = editableSubject.trim();
              const body = editableBody.trim();
              post('/api/temple/campaigns/send', {
                campaign_product_id: campaignProduct.id,
                campaign_product_name: campaignProduct.name || '',
                template_key: templateKey,
                target_product_ids: targetProducts.map((p) => ({ product_id: p.product_id, variation_id: p.variation_id || null, product_name: p.product_name || null })),
                subject,
                body,
                mode: 'test',
              })
                .then((data) => {
                  setTestSuccess(`Test email sent to ${data?.to_email || 'your email address'}.`);
                  setSending(false);
                })
                .catch((e) => {
                  setSendError(e?.message || 'Test send failed');
                  setSending(false);
                });
            }}
            disabled={!canSend || sending || countdown !== null}
          >
            Send test email
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={startCountdown}
            disabled={sending || countdown !== null || !!sendSuccess}
          >
            Send email
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={recipientsDialogOpen} onClose={() => setRecipientsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Recipients
          <IconButton aria-label="Close" onClick={() => setRecipientsDialogOpen(false)} size="small" sx={{ mr: -1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {recipientsResult?.recipients?.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...(recipientsResult.recipients || [])]
                  .sort((a, b) => {
                    const na = (a.name || '').toLowerCase().trim();
                    const nb = (b.name || '').toLowerCase().trim();
                    return na.localeCompare(nb) || (a.email || '').localeCompare(b.email || '');
                  })
                  .map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.name || '—'}</TableCell>
                    <TableCell>{r.email}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography color="text.secondary">No recipients.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecipientsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
